import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import './LinkParentApproval.css';

export default function LinkParentApproval() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [preview, setPreview] = useState(null);      // { parent_name, parent_email, gym_name, expires_at }
  const [loading, setLoading] = useState(true);
  const [tokenError, setTokenError] = useState('');   // expired / used / not found
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [actionError, setActionError] = useState('');

  const storedUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const isLoggedIn = !!storedUser?.token;
  const isAthlete = storedUser?.role === 'athlete';

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/parent/link-token/preview/${token}`)
      .then(res => setPreview(res.data))
      .catch(err => setTokenError(err.response?.data?.error || 'This link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleApprove = async () => {
    setSubmitting(true);
    setActionError('');
    try {
      await axios.post(`${API_BASE_URL}/api/parent/link-token/use/${token}`);
      setDone(true);
    } catch (err) {
      setActionError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = () => navigate('/');

  // ── Loading ──
  if (loading) {
    return (
      <div className="lpa-container">
        <div className="lpa-card lpa-loading">Loading link details…</div>
      </div>
    );
  }

  // ── Token error (expired / used / not found) ──
  if (tokenError) {
    return (
      <div className="lpa-container">
        <div className="lpa-card lpa-error-card">
          <div className="lpa-icon lpa-icon-error">⚠️</div>
          <h2>Link Unavailable</h2>
          <p>{tokenError}</p>
          <Link to="/" className="lpa-btn lpa-btn-secondary">Go to Home</Link>
        </div>
      </div>
    );
  }

  // ── Success ──
  if (done) {
    return (
      <div className="lpa-container">
        <div className="lpa-card lpa-success-card">
          <div className="lpa-icon">✅</div>
          <h2>All Set!</h2>
          <p>
            You've successfully linked your account to <strong>{preview.parent_name}</strong>'s
            parent profile. They can now view your schedule and progress.
          </p>
          <button className="lpa-btn lpa-btn-primary" onClick={() => navigate('/')}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── Not logged in ──
  if (!isLoggedIn) {
    return (
      <div className="lpa-container">
        <div className="lpa-card">
          <div className="lpa-icon">🔗</div>
          <h2>Parent Link Request</h2>
          <div className="lpa-parent-info">
            <p>
              <strong>{preview.parent_name}</strong> ({preview.parent_email}) at{' '}
              <strong>{preview.gym_name}</strong> wants to link to your athlete profile
              as a parent / guardian.
            </p>
          </div>
          <p className="lpa-login-note">
            Please log in to your <strong>athlete account</strong> to approve or decline this request.
          </p>
          <Link
            to={`/login?redirect=/link-parent/${token}`}
            className="lpa-btn lpa-btn-primary"
          >
            Log In to Approve
          </Link>
          <p className="lpa-expires">
            Link expires: {new Date(preview.expires_at).toLocaleString()}
          </p>
        </div>
      </div>
    );
  }

  // ── Logged in but not an athlete ──
  if (!isAthlete) {
    return (
      <div className="lpa-container">
        <div className="lpa-card lpa-error-card">
          <div className="lpa-icon">ℹ️</div>
          <h2>Athlete Account Required</h2>
          <p>
            This link can only be approved by an <strong>athlete account</strong>.
            You're currently logged in as <strong>{storedUser.role}</strong>.
          </p>
          <p>
            Please log in with the athlete's account to approve this parent link request
            from <strong>{preview.parent_name}</strong>.
          </p>
          <Link to="/" className="lpa-btn lpa-btn-secondary">Go to Home</Link>
        </div>
      </div>
    );
  }

  // ── Logged in as athlete — main approval UI ──
  return (
    <div className="lpa-container">
      <div className="lpa-card">
        <div className="lpa-icon">👨‍👧‍👦</div>
        <h2>Parent Link Request</h2>
        <div className="lpa-parent-info">
          <div className="lpa-parent-row">
            <span className="lpa-label">Name</span>
            <span className="lpa-value">{preview.parent_name}</span>
          </div>
          <div className="lpa-parent-row">
            <span className="lpa-label">Email</span>
            <span className="lpa-value">{preview.parent_email}</span>
          </div>
          <div className="lpa-parent-row">
            <span className="lpa-label">Gym</span>
            <span className="lpa-value">{preview.gym_name}</span>
          </div>
        </div>

        <p className="lpa-description">
          By approving, <strong>{preview.parent_name}</strong> will be able to view your
          athlete profile, schedule, and progress updates.
        </p>

        {actionError && <p className="lpa-action-error">{actionError}</p>}

        <div className="lpa-actions">
          <button className="lpa-btn lpa-btn-decline" onClick={handleDecline} disabled={submitting}>
            Decline
          </button>
          <button className="lpa-btn lpa-btn-primary" onClick={handleApprove} disabled={submitting}>
            {submitting ? 'Approving…' : 'Approve Link'}
          </button>
        </div>

        <p className="lpa-expires">
          Link expires: {new Date(preview.expires_at).toLocaleString()}
        </p>
      </div>
    </div>
  );
}
